/**
 * Ronda de inspeção de pneus no app (pátio, celular):
 *
 *   veículo → mapa → posição → sulco → pressão → condição → foto/observação →
 *   próximo pneu … → finalizar → resumo (N inspecionados: X normais, Y atenção, Z críticos)
 *
 * Mesmo jeito do checklist: um passo por tela, alvos de toque grandes, rascunho
 * guardado no aparelho (se a rede cair, nada do que foi medido se perde). A
 * ronda é gravada de uma vez (tudo ou nada) e as fotos sobem depois.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TireInspectionRoundResultDto, TireMapDto, TireMapPositionDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { IconCamera, IconCheck, IconChevronLeft, IconTire } from "../../components/icons";
import { TireMap } from "../../features/tires/TireMap";
import { emptyInspection, InspectionFieldsForm, toNumber, type InspectionFields } from "../../features/tires/InspectTireModal";
import { compressPhoto } from "../../features/maintenance/photo-compress";
import { ConditionBadge, fmtKm, fmtMm, useTireCapabilities } from "../../features/tires/tire-ui";

type Draft = Record<string, InspectionFields>; // por positionId

const draftKey = (plate: string) => `bi:tire-round:${plate}`;
function loadDraft(plate: string): { km: string; items: Draft } {
  try {
    const raw = localStorage.getItem(draftKey(plate));
    return raw ? JSON.parse(raw) : { km: "", items: {} };
  } catch {
    return { km: "", items: {} };
  }
}
function saveDraft(plate: string, d: { km: string; items: Draft } | null) {
  try {
    if (d) localStorage.setItem(draftKey(plate), JSON.stringify(d)); else localStorage.removeItem(draftKey(plate));
  } catch { /* armazenamento indisponível: segue sem rascunho */ }
}

const preenchido = (f?: InspectionFields) => !!f && (f.tread.trim() !== "" || f.pressure.trim() !== "" || f.problems.length > 0 || f.condition !== "normal");

export function PortalTireInspectionPage() {
  const [params, setParams] = useSearchParams();
  const plate = (params.get("placa") ?? "").toUpperCase();
  const { can, loading } = useTireCapabilities();

  if (loading) return <p className="text-sm text-gray-500">Carregando…</p>;
  if (!can("tires.inspect")) {
    return (
      <div className="card">
        <EmptyState icon={<IconTire className="w-6 h-6" />} title="Sem permissão para inspecionar pneus"
          description="Peça a um gestor para liberar a inspeção de pneus para o seu usuário." />
      </div>
    );
  }
  return plate ? <Ronda plate={plate} onTrocar={() => setParams({})} /> : <EscolherVeiculo onEscolher={(p) => setParams({ placa: p })} />;
}

function EscolherVeiculo({ onEscolher }: { onEscolher: (p: string) => void }) {
  const [q, setQ] = useState("");
  const veiculos = useQuery({
    queryKey: ["maintenance", "vehicles", q],
    queryFn: () => api.get<{ items: Array<{ plate: string; vehicleTypeName: string | null; brand?: string | null }> }>(`/maintenance/vehicles?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
  });
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-gray-900">Inspeção de pneus</h1>
        <p className="text-sm text-gray-500">Escolha o veículo. Depois, toque em cada pneu do mapa.</p>
      </div>
      <input className="input w-full text-lg uppercase font-mono" autoFocus placeholder="Placa" value={q}
        onChange={(e) => setQ(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} />
      <div className="card divide-y divide-surface-border">
        {(veiculos.data?.items ?? []).map((v) => (
          <button key={v.plate} className="w-full text-left px-4 py-3 min-h-[56px] flex items-center justify-between" onClick={() => onEscolher(v.plate)}>
            <span className="font-mono font-semibold text-gray-900">{v.plate}</span>
            <span className="text-xs text-gray-500">{v.vehicleTypeName ?? ""}</span>
          </button>
        ))}
        {q.length >= 2 && veiculos.data && veiculos.data.items.length === 0 && <p className="px-4 py-3 text-sm text-gray-500">Nenhum veículo da frota própria com esta placa.</p>}
      </div>
    </div>
  );
}

function Ronda({ plate, onTrocar }: { plate: string; onTrocar: () => void }) {
  const qc = useQueryClient();
  const mapa = useQuery({
    queryKey: ["tires", "map", plate],
    queryFn: () => api.get<TireMapDto>(`/fleet/vehicles/${encodeURIComponent(plate)}/tire-map`),
    retry: false,
  });
  const inicial = useMemo(() => loadDraft(plate), [plate]);
  const [km, setKm] = useState(inicial.km);
  const [itens, setItens] = useState<Draft>(inicial.items);
  const [fotos, setFotos] = useState<Record<string, File>>({});
  const [atual, setAtual] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<TireInspectionRoundResultDto | null>(null);
  const [fotosFalharam, setFotosFalharam] = useState(0);

  useEffect(() => { if (!resultado) saveDraft(plate, { km, items: itens }); }, [plate, km, itens, resultado]);
  useEffect(() => {
    if (mapa.data?.odometer && km === "") setKm(String(mapa.data.odometer.km));
  }, [mapa.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const comPneu: TireMapPositionDto[] = useMemo(
    () => (mapa.data?.axles ?? []).flatMap((a) => a.positions).filter((p) => p.tire),
    [mapa.data],
  );
  const idx = atual ? comPneu.findIndex((p) => p.id === atual) : -1;
  const pos = idx >= 0 ? comPneu[idx]! : null;
  const feitos = comPneu.filter((p) => preenchido(itens[p.id])).length;

  const finalizar = useMutation({
    mutationFn: async () => {
      const items = comPneu.filter((p) => preenchido(itens[p.id])).map((p) => {
        const f = itens[p.id]!;
        return {
          tireId: p.tire!.id, positionId: p.id, treadDepthMm: toNumber(f.tread), pressurePsi: toNumber(f.pressure),
          condition: f.condition, problems: f.problems, notes: f.notes.trim() || null,
        };
      });
      const r = await api.post<TireInspectionRoundResultDto>(`/fleet/vehicles/${encodeURIComponent(plate)}/tire-inspections`, {
        odometerKm: km ? Number(km) : null, items,
      });
      // Fotos depois da ronda gravada: falha de foto não desfaz a inspeção.
      let falhas = 0;
      for (const ins of r.inspections) {
        const p = comPneu.find((x) => x.tire?.id === ins.tireId);
        const foto = p ? fotos[p.id] : undefined;
        if (!foto) continue;
        try {
          await api.upload(`/fleet/tire-inspections/${ins.id}/attachments`, new File([await compressPhoto(foto)], foto.name || "pneu.jpg", { type: "image/jpeg" }));
        } catch { falhas++; }
      }
      setFotosFalharam(falhas);
      return r;
    },
    onSuccess: (r) => {
      saveDraft(plate, null);
      qc.invalidateQueries({ queryKey: ["tires"] });
      setResultado(r);
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível gravar a ronda. Os dados continuam guardados neste aparelho."),
  });

  if (mapa.isLoading) return <p className="text-sm text-gray-500">Carregando {plate}…</p>;
  if (mapa.isError || !mapa.data) {
    return (
      <div className="card"><EmptyState icon={<IconTire className="w-6 h-6" />} title={`Veículo ${plate} não encontrado`}
        action={<button className="btn-secondary" onClick={onTrocar}>Escolher outro</button>} /></div>
    );
  }
  const m = mapa.data;

  if (resultado) {
    const s = resultado.summary;
    return (
      <div className="card p-6 text-center space-y-4">
        <div className="mx-auto w-14 h-14 rounded-full bg-tm-500 text-white flex items-center justify-center"><IconCheck className="w-8 h-8" /></div>
        <p className="text-lg font-semibold text-gray-900">Inspeção concluída</p>
        <p className="text-sm text-gray-600">{s.total} pneu(s) inspecionado(s) em {resultado.vehiclePlate}</p>
        <div className="flex justify-center gap-2 flex-wrap">
          <span className="badge-green">{s.normal} normal(is)</span>
          <span className="badge-amber">{s.attention} atenção</span>
          <span className="badge-red">{s.critical} crítico(s)</span>
        </div>
        {fotosFalharam > 0 && <p className="text-xs text-warning-700">{fotosFalharam} foto(s) não subiram. A inspeção foi gravada; anexe pela ficha do pneu.</p>}
        <div className="grid gap-2">
          <Link className="btn-primary justify-center" to={`/frota/pneus?aba=inspecoes&placa=${encodeURIComponent(resultado.vehiclePlate)}`}>Ver relatório</Link>
          <button className="btn-secondary justify-center" onClick={onTrocar}>Inspecionar outro veículo</button>
        </div>
      </div>
    );
  }

  if (!m.configuration) {
    return (
      <div className="card"><EmptyState icon={<IconTire className="w-6 h-6" />} title={`${plate} ainda não tem mapa de pneus`}
        description="Um gestor precisa definir a configuração de eixos no Raio-X do veículo (aba Pneus)."
        action={<button className="btn-secondary" onClick={onTrocar}>Escolher outro</button>} /></div>
    );
  }
  if (comPneu.length === 0) {
    return (
      <div className="card"><EmptyState icon={<IconTire className="w-6 h-6" />} title={`Nenhum pneu montado em ${plate} no SIAG`}
        description="Registre as montagens (Movimentar pneu) ou importe o ponto de partida da Finatto antes da ronda."
        action={<button className="btn-secondary" onClick={onTrocar}>Escolher outro</button>} /></div>
    );
  }

  // ── Uma posição por tela ──────────────────────────────────────────────────
  if (pos) {
    const f = itens[pos.id] ?? emptyInspection();
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button className="btn-ghost p-2" onClick={() => setAtual(null)} aria-label="Voltar ao mapa"><IconChevronLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{pos.label}</p>
            <p className="text-xs text-gray-500">{idx + 1} de {comPneu.length}</p>
          </div>
        </div>
        <div className="card p-4 space-y-4">
          <div className="flex items-baseline justify-between">
            <p className="font-mono font-semibold text-gray-900">Pneu {pos.tire!.code}</p>
            <p className="text-xs text-gray-500">último: {fmtMm(pos.tire!.treadMm)}</p>
          </div>
          <InspectionFieldsForm value={f} onChange={(v) => setItens({ ...itens, [pos.id]: v })} compact />
          <label className="btn-secondary w-full justify-center cursor-pointer">
            <IconCamera className="w-4 h-4" /> {fotos[pos.id] ? "Trocar foto" : "Foto (opcional)"}
            <input type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { const file = e.target.files?.[0]; if (file) setFotos({ ...fotos, [pos.id]: file }); }} />
          </label>
          {fotos[pos.id] && <p className="text-xs text-gray-500 truncate">{fotos[pos.id]!.name}</p>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button className="btn-secondary justify-center py-3" disabled={idx === 0} onClick={() => setAtual(comPneu[idx - 1]!.id)}>Anterior</button>
          {idx < comPneu.length - 1
            ? <button className="btn-primary justify-center py-3" onClick={() => setAtual(comPneu[idx + 1]!.id)}>Próximo</button>
            : <button className="btn-primary justify-center py-3" onClick={() => setAtual(null)}>Revisar</button>}
        </div>
      </div>
    );
  }

  // ── Mapa + KM + finalizar ─────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Inspeção · <span className="font-mono">{plate}</span></h1>
          <p className="text-sm text-gray-500">{m.configuration.name} · {feitos} de {comPneu.length} pneu(s) preenchido(s)</p>
        </div>
        <button className="btn-ghost text-sm" onClick={onTrocar}>Trocar</button>
      </div>

      {!m.vehicle.isTrailer && (
        <label className="block card p-4">
          <span className="text-xs text-gray-500">KM do veículo</span>
          <input className="input w-full mt-0.5 text-lg" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value.replace(/\D/g, ""))} />
          {m.odometer && <span className="block text-xs text-gray-400 mt-1">Sugerido: {fmtKm(m.odometer.km)} · {m.odometer.sourceLabel}</span>}
        </label>
      )}

      <div className="card p-3">
        <TireMap axles={m.axles} kind={m.vehicle.isTrailer ? "trailer" : "tractor"} onSelect={(p) => p.tire && setAtual(p.id)} />
      </div>

      {feitos > 0 && (
        <div className="card divide-y divide-surface-border">
          {comPneu.filter((p) => preenchido(itens[p.id])).map((p) => (
            <button key={p.id} className="w-full text-left px-4 py-2.5 flex items-center gap-2" onClick={() => setAtual(p.id)}>
              <span className="text-sm text-gray-700 flex-1 truncate">{p.label}</span>
              <span className="text-xs text-gray-500">{itens[p.id]!.tread ? `${itens[p.id]!.tread} mm` : ""}</span>
              <ConditionBadge condition={itens[p.id]!.condition} />
            </button>
          ))}
        </div>
      )}

      {erro && <p className="text-sm text-danger-600">{erro}</p>}
      <div className="grid gap-2">
        {feitos === 0
          ? <button className="btn-primary justify-center py-3" onClick={() => setAtual(comPneu[0]!.id)}>Começar pelo primeiro pneu</button>
          : <button className="btn-primary justify-center py-3" disabled={finalizar.isPending} onClick={() => { setErro(null); finalizar.mutate(); }}>
              {finalizar.isPending ? "Gravando…" : `Finalizar (${feitos} pneu${feitos > 1 ? "s" : ""})`}
            </button>}
      </div>
    </div>
  );
}
